import type { ComplianceImportOptions, ImportError } from './types';

export const MAX_CSV_BYTES = 5 * 1024 * 1024;
export const MAX_CSV_COLUMNS = 60;
export const MAX_CSV_DATA_ROWS = 5_000;

/** Parse RFC-4180 input without throwing: malformed input remains draft data. */
export function parseCsv(
  text: string,
  options: ComplianceImportOptions = {},
): string[][] | ImportError {
  const byteLength = options.byteLength ?? new TextEncoder().encode(text).byteLength;
  if (byteLength > MAX_CSV_BYTES) {
    return { ok: false, code: 'TEXT_TOO_LARGE', message: 'The CSV is larger than 5 MB.' };
  }

  const rows: string[][] = [];
  let row: string[] = [];
  let value = '';
  let inQuotes = false;
  let afterClosingQuote = false;

  for (let index = 0; index < text.length; index += 1) {
    const character = text[index];
    if (inQuotes) {
      if (character === '"' && text[index + 1] === '"') {
        value += '"';
        index += 1;
      } else if (character === '"') {
        inQuotes = false;
        afterClosingQuote = true;
      } else {
        value += character;
      }
      continue;
    }
    if (afterClosingQuote) {
      if (character === ',') {
        row.push(value);
        value = '';
        afterClosingQuote = false;
        continue;
      }
      if (character === '\n' || character === '\r') {
        row.push(value);
        rows.push(row);
        row = [];
        value = '';
        afterClosingQuote = false;
        if (character === '\r' && text[index + 1] === '\n') index += 1;
        continue;
      }
      return {
        ok: false,
        code: 'CSV_MALFORMED',
        message: 'A quoted field must be followed by a delimiter or line end.',
      };
    }
    if (character === '"') {
      if (value !== '')
        return {
          ok: false,
          code: 'CSV_MALFORMED',
          message: 'The CSV has a quote in an unquoted field.',
        };
      inQuotes = true;
    } else if (character === ',') {
      row.push(value);
      value = '';
    } else if (character === '\n') {
      row.push(value.endsWith('\r') ? value.slice(0, -1) : value);
      if (row.some((cell) => cell !== '')) rows.push(row);
      row = [];
      value = '';
    } else {
      value += character;
    }
  }

  if (inQuotes)
    return { ok: false, code: 'CSV_MALFORMED', message: 'The CSV has an unclosed quoted field.' };
  if (value !== '' || row.length > 0) {
    row.push(value);
    if (row.some((cell) => cell !== '')) rows.push(row);
  }
  if (rows.length === 0)
    return { ok: false, code: 'CSV_MALFORMED', message: 'The CSV has no header row.' };
  if ((rows[0]?.length ?? 0) > MAX_CSV_COLUMNS)
    return { ok: false, code: 'TOO_MANY_COLUMNS', message: 'The CSV has more than 60 columns.' };
  if (rows.length - 1 > MAX_CSV_DATA_ROWS)
    return { ok: false, code: 'TOO_MANY_ROWS', message: 'The CSV has more than 5,000 data rows.' };
  return rows;
}
